// HubSpot Tracking Functions
// Main API functions for tracking page views, events, and user identification

import logger from '@pray/shared/utils/logger';

import { pushToHubSpot, setPath } from './core';
import type { HubSpotUserIdentity, HubSpotPageViewOptions, HubSpotCustomBehavioralEvent } from './types';

/**
 * Track a page view in HubSpot
 * For single-page applications, this should be called on route changes
 *
 * @param options - Configuration options for page tracking
 * @param options.path - The path to track (defaults to current pathname)
 *
 * @example
 * // Track current page
 * trackPageView();
 *
 * // Track specific path
 * trackPageView({ path: '/dashboard' });
 */
export function trackPageView(options: HubSpotPageViewOptions = {}): void {
  const { path } = options;

  try {
    // Set path if provided, otherwise use current location
    if (path) {
      setPath(path);
    } else if (typeof window !== 'undefined' && window.location) {
      setPath(window.location.pathname);
    }

    // Track the page view
    pushToHubSpot(['trackPageView']);
  } catch (error) {
    logger.error('HubSpot: Failed to track page view', error);
  }
}

/**
 * Identify a visitor in HubSpot
 * This associates subsequent tracking events with the identified user
 *
 * @param identity - User identity information
 * @param identity.email - User's email address (required)
 * @param identity.* - Additional user properties
 *
 * @example
 * // Basic identification
 * identifyUser({ email: 'user@example.com' });
 *
 * // With additional properties
 * identifyUser({
 *   email: 'user@example.com',
 *   firstName: 'John',
 *   lastName: 'Doe',
 * });
 */
export function identifyUser(identity: HubSpotUserIdentity): void {
  if (!identity.email) {
    logger.info('HubSpot: Email is required for user identification');
    return;
  }

  try {
    // Identify the user with their email and additional properties
    pushToHubSpot(['identify', identity]);
  } catch (error) {
    logger.error('HubSpot: Failed to identify user', error);
  }
}

// Use this function only if user has previously been identified with email
export function additionalIdentifyUser(identity: Omit<HubSpotUserIdentity, 'email'>): void {
  try {
    // Identify the user with their email and additional properties
    pushToHubSpot(['identify', identity]);
  } catch (error) {
    logger.error('HubSpot: Failed to identify user', error);
  }
}

/**
 * Track a custom event in HubSpot
 *
 * @param eventName - Name of the custom event
 * @param properties - Optional properties to associate with the event
 *
 * @example
 * trackEvent('button_clicked', { buttonName: 'Subscribe' });
 * trackEvent('form_submitted', { formId: 'contact-form' });
 */
export function trackEvent(
  eventName: HubSpotCustomBehavioralEvent['name'],
  properties?: HubSpotCustomBehavioralEvent['properties']
): void {
  if (!eventName) {
    logger.info('HubSpot: Event name is required');
    return;
  }

  try {
    pushToHubSpot(['trackCustomBehavioralEvent', { name: eventName, properties }]);
  } catch (error) {
    logger.error('HubSpot: Failed to track event', error);
  }
}

/**
 * Utility for React Router integration
 * Call this function on route changes in single-page applications
 * @example
 * // In a React Router useEffect
 * useEffect(() => {
 *   onRouteChange(location.pathname);
 * }, [location.pathname]);
 */
export function onRouteChange(path: string): void {
  trackPageView({ path });
}
